/**
 * Sessions Store Slice
 *
 * Zustand slice for Application Session lifecycle state management.
 * Domain: ApplicationContext > SessionPurchaseAggregate
 *
 * GW-8285: Authoritative session slice — split out of the former
 * 1781-line `applicationsSlice.ts` (GW-8255 FR-2/Phase 4, Task 28). Owns
 * everything session-lifecycle: create/launch, list/search/paginate,
 * revoke, expire, renew, and the Session Companion Bar's
 * activeLaunchedSession/appTabRef fields. `applicationsSlice.ts` now owns
 * only Application CRUD + TOS state; it no longer has any session actions.
 *
 * This replaces the previous, unused `sessionsSlice.ts` (a thinner
 * "activeSessions/sessionHistory" duplicate with zero consumers) as the
 * one authoritative session slice.
 */

import {
  clearError as clearOpError,
  clearErrorStatus,
  isOperationLoading,
  setError,
  setErrorStatus,
  setLoaded,
  setLoading,
} from '@gw/ui';
import { SessionStatus, type SessionStatusType } from '@gw/ui/types';

import { ApiError } from '@/api/apiError';
import { applicationsApi } from '@/api/applications';
import { sessionsApi } from '@/api/sessions';
// GW-6534: Read currentOrg.id from the Zustand store instead of
// localStorage; throw a typed error when no current org is set rather than
// silently coercing to "" on session DTOs (which is what the localStorage
// fallback used to do).
import { getCurrentOrgIdFromStore } from '@/stores/orgIdAccessor';

import type {
  ApplicationSession,
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
  SessionFilters,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface SessionsSlice {
  // State properties
  sessions: ApplicationSession[];
  sessionsPagination: import('@gw/ui/types').PaginationMetadata | null;
  sessionsSearchQuery: string;
  sessionsStatusFilter: SessionStatusType | undefined;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;
  activeLaunchedSession: ApplicationSession | null;
  appTabRef: Window | null;

  // Session lifecycle actions
  createSession: (appId: string, durationMinutes: number) => Promise<ApplicationSession>;
  createSessionAndLaunch: (appId: string, durationMinutes: number) => Promise<ApplicationSession>;
  fetchUserSessions: (appId: string, filters?: SessionFilters) => Promise<void>;
  fetchAllUserSessions: (filters?: SessionFilters) => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  searchSessions: (
    query: string,
    additionalFilters?: Omit<SessionFilters, 'search'>
  ) => Promise<void>;
  resetSessionsPagination: () => void;
  revokeSession: (appId: string, sessionId: string) => Promise<void>;
  expireSession: (sessionId: string) => void;
  renewSession: (appId: string, sessionId: string, additionalMinutes: number) => Promise<void>;

  // Utility actions
  clearError: (operation?: string) => void;
  clearActiveLaunchedSession: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialSessionsState = {
  sessions: [],
  sessionsPagination: null,
  sessionsSearchQuery: '',
  sessionsStatusFilter: undefined,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},
  activeLaunchedSession: null,
  appTabRef: null,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createSessionsSlice: StateCreator<SessionsSlice, [], [], SessionsSlice> = (
  set,
  get
) => ({
  ...initialSessionsState,

  // ==========================================================================
  // Session Lifecycle
  // ==========================================================================

  createSession: async (appId: string, durationMinutes: number) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'createSession', true),
      errors: clearOpError(currentState.errors, 'createSession'),
    });

    try {
      // GW-505: Use sessionsApi (POST /sessions) instead of applicationsApi
      // applicationsApi.createSession POSTs to /applications/{appId}/sessions which doesn't exist
      // GW-6534: Read currentOrg.id from Zustand BEFORE the API call. If no
      // org is selected, fail loudly instead of silently storing '' on the
      // session DTO (the previous localStorage fallback). This surfaces the
      // bug in Sentry as an InvalidOrgIdError-class regression rather than
      // hiding it behind a 500 from a follow-up call.
      const orgId = getCurrentOrgIdFromStore();
      if (!orgId) {
        throw new ApiError(
          'No organization selected — cannot create session without org context',
          400
        );
      }
      const response = await sessionsApi.createSession(appId, durationMinutes);

      // Transform CreateSessionResponse to ApplicationSession (matching createSessionAndLaunch)
      const session: ApplicationSession = {
        id: response.session_id,
        applicationId: appId,
        userId: '', // Will be set by backend based on auth token
        orgId: orgId,
        startTime: new Date().toISOString(),
        expiresAt: new Date(response.expires_at * 1000).toISOString(),
        durationMinutes: durationMinutes,
        baseCost: response.tokens_consumed,
        markupPercent: 0,
        totalCost: response.tokens_consumed,
        smartTokens: response.tokens_consumed,
        jwt: response.session_jwt,
        launchUrl: response.launch_url,
        status: SessionStatus.Active,
        createdAt: new Date().toISOString(),
      };

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'createSession', true);

      set({
        sessions: [...state.sessions, session],
        isLoading: setLoading(state.isLoading, 'createSession', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      // GW-6195: Refresh org token balance after successful session creation
      // Fire-and-forget to avoid blocking the UI. fetchOrganizationBalance
      // lives on organizationTokensSlice — read via the combined store at
      // call time (same cross-slice-access pattern as organizationTokensSlice
      // itself uses for currentOrg) since SessionsSlice's own type has no
      // knowledge of sibling slices.
      const refreshOrgId = session.orgId || orgId;
      if (refreshOrgId) {
        const liveState = get() as unknown as {
          fetchOrganizationBalance?: (orgId: string) => Promise<void>;
        };
        void liveState.fetchOrganizationBalance?.(refreshOrgId).catch(() => {
          // Silently fail - balance will refresh on next org switch or page load
        });
      }

      return session;
    } catch (error) {
      console.error('[SessionsSlice] Failed to create session:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to create session';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'createSession', false);

      set({
        isLoading: setLoading(state.isLoading, 'createSession', false),
        errors: setError(state.errors, 'createSession', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  createSessionAndLaunch: async (appId: string, durationMinutes: number) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'createSessionAndLaunch', true),
      errors: clearOpError(currentState.errors, 'createSessionAndLaunch'),
    });

    try {
      // GW-6534: Read currentOrg.id from Zustand BEFORE the API call. If no
      // org is selected, fail loudly instead of silently storing '' on the
      // session DTO. See createSession comment above for full rationale.
      const orgId = getCurrentOrgIdFromStore();
      if (!orgId) {
        throw new ApiError(
          'No organization selected — cannot launch session without org context',
          400
        );
      }
      // Call the new sessions API endpoint
      const response = await sessionsApi.createSession(appId, durationMinutes);

      // Create ApplicationSession object from response
      const session: ApplicationSession = {
        id: response.session_id,
        applicationId: appId,
        userId: '', // Will be set by backend based on auth token
        orgId: orgId,
        startTime: new Date().toISOString(),
        expiresAt: new Date(response.expires_at * 1000).toISOString(),
        durationMinutes: durationMinutes,
        baseCost: response.tokens_consumed,
        markupPercent: 0,
        totalCost: response.tokens_consumed,
        smartTokens: response.tokens_consumed,
        jwt: response.session_jwt,
        launchUrl: response.launch_url,
        status: SessionStatus.Active,
        createdAt: new Date().toISOString(),
      };

      // Container app — return session without opening tab (navigation handled by calling component)
      if (response.session_type === 'container' && response.container_session_id) {
        const state = get();
        const loadedState = setLoaded(
          state.hasLoaded,
          state.lastLoaded,
          'createSessionAndLaunch',
          true
        );

        const containerSession: ApplicationSession = {
          ...session,
          containerSessionId: response.container_session_id,
          accessMode: response.access_mode,
          containerStatus: response.container_status,
          sessionType: response.session_type,
        };

        set({
          sessions: [...state.sessions, containerSession],
          isLoading: setLoading(state.isLoading, 'createSessionAndLaunch', false),
          hasLoaded: loadedState.hasLoaded,
          lastLoaded: loadedState.lastLoaded,
        });

        // GW-6195: Refresh org token balance after successful session creation
        // Fire-and-forget to avoid blocking the UI
        const refreshOrgId = containerSession.orgId || orgId;
        if (refreshOrgId) {
          const liveState = get() as unknown as {
            fetchOrganizationBalance?: (orgId: string) => Promise<void>;
          };
          void liveState.fetchOrganizationBalance?.(refreshOrgId).catch(() => {
            // Silently fail - balance will refresh on next org switch or page load
          });
        }

        return containerSession;
      }

      // Standard app: Open new tab SYNCHRONOUSLY (popup blocker prevention)
      // Must happen in same execution context as user click
      const newTab = window.open(response.launch_url, '_blank');

      if (!newTab) {
        throw new Error('Failed to open application tab. Please check popup blocker settings.');
      }

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'createSessionAndLaunch',
        true
      );

      set({
        sessions: [...state.sessions, session],
        activeLaunchedSession: session,
        appTabRef: newTab,
        isLoading: setLoading(state.isLoading, 'createSessionAndLaunch', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      // GW-6195: Refresh org token balance after successful session creation
      // Fire-and-forget to avoid blocking the UI (window.open already executed synchronously)
      const refreshOrgId = session.orgId || orgId;
      if (refreshOrgId) {
        const liveState = get() as unknown as {
          fetchOrganizationBalance?: (orgId: string) => Promise<void>;
        };
        void liveState.fetchOrganizationBalance?.(refreshOrgId).catch(() => {
          // Silently fail - balance will refresh on next org switch or page load
        });
      }

      return session;
    } catch (error) {
      const state = get();

      // GW-6160: Handle NO_ORG_CONTEXT with a friendly user-facing message
      // GW-6128: baseApiClient now auto-retries NO_ORG_CONTEXT with forceRefresh.
      // This path is only reached if the retry also fails (user truly has no org context).
      if (
        error instanceof ApiError &&
        error.status === 403 &&
        (error.data as { code?: string } | undefined)?.code === 'NO_ORG_CONTEXT'
      ) {
        const friendlyMessage =
          'Your account is not part of an organization yet. Please complete onboarding to launch a session.';
        const loadedState = setLoaded(
          state.hasLoaded,
          state.lastLoaded,
          'createSessionAndLaunch',
          false
        );

        set({
          isLoading: setLoading(state.isLoading, 'createSessionAndLaunch', false),
          errors: setError(state.errors, 'createSessionAndLaunch', friendlyMessage),
          errorStatuses: setErrorStatus(state.errorStatuses, 'createSessionAndLaunch', 403),
          hasLoaded: loadedState.hasLoaded,
          lastLoaded: loadedState.lastLoaded,
        });
        throw new Error(friendlyMessage);
      }

      // Log unhandled errors (NO_ORG_CONTEXT already handled above)
      console.error('[SessionsSlice] Failed to create and launch session:', error);

      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create and launch session';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'createSessionAndLaunch',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'createSessionAndLaunch', false),
        errors: setError(state.errors, 'createSessionAndLaunch', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  fetchUserSessions: async (appId: string, filters?: SessionFilters) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchUserSessions', true),
      errors: clearOpError(currentState.errors, 'fetchUserSessions'),
    });

    try {
      const sessions = await applicationsApi.listUserSessions(appId, filters);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchUserSessions', true);

      set({
        sessions,
        isLoading: setLoading(state.isLoading, 'fetchUserSessions', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[SessionsSlice] Failed to fetch sessions:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load sessions';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchUserSessions', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchUserSessions', false),
        errors: setError(state.errors, 'fetchUserSessions', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  fetchAllUserSessions: async (filters?: SessionFilters) => {
    const currentState = get();

    // Merge search query from state with filters
    const finalFilters: SessionFilters = {
      ...filters,
      search: filters?.search ?? currentState.sessionsSearchQuery,
    };

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchAllUserSessions', true),
      errors: clearOpError(currentState.errors, 'fetchAllUserSessions'),
      // Persist the status filter so loadMoreSessions can forward it on subsequent pages
      sessionsStatusFilter: filters?.status,
    });

    try {
      const response = await sessionsApi.listSessions(finalFilters);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchAllUserSessions',
        true
      );

      set({
        sessions: response.sessions,
        sessionsPagination: response.pagination,
        isLoading: setLoading(state.isLoading, 'fetchAllUserSessions', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[SessionsSlice] Failed to fetch all user sessions:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load sessions';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchAllUserSessions',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'fetchAllUserSessions', false),
        errors: setError(state.errors, 'fetchAllUserSessions', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  loadMoreSessions: async () => {
    const currentState = get();

    // Guard: Cannot load more if no pagination metadata or no next cursor
    if (!currentState.sessionsPagination || !currentState.sessionsPagination.nextCursor) {
      return;
    }

    // Guard: Don't load if already loading
    if (isOperationLoading(currentState.isLoading, 'loadMoreSessions')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'loadMoreSessions', true),
      errors: clearOpError(currentState.errors, 'loadMoreSessions'),
    });

    try {
      const filters: SessionFilters = {
        cursor: currentState.sessionsPagination.nextCursor,
        search: currentState.sessionsSearchQuery || undefined,
        status: currentState.sessionsStatusFilter,
        limit: currentState.sessionsPagination.limit,
      };

      const response = await sessionsApi.listSessions(filters);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'loadMoreSessions', true);

      set({
        sessions: [...state.sessions, ...response.sessions], // Append new sessions
        sessionsPagination: response.pagination,
        isLoading: setLoading(state.isLoading, 'loadMoreSessions', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[SessionsSlice] Failed to load more sessions:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more sessions';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'loadMoreSessions', false);

      set({
        isLoading: setLoading(state.isLoading, 'loadMoreSessions', false),
        errors: setError(state.errors, 'loadMoreSessions', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  searchSessions: async (query: string, additionalFilters?: Omit<SessionFilters, 'search'>) => {
    const currentState = get();

    set({
      sessionsSearchQuery: query,
      isLoading: setLoading(currentState.isLoading, 'searchSessions', true),
      errors: clearOpError(currentState.errors, 'searchSessions'),
    });

    try {
      const filters: SessionFilters = {
        ...additionalFilters,
        search: query || undefined,
        limit: additionalFilters?.limit || 20, // Default limit for new search
      };

      const response = await sessionsApi.listSessions(filters);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'searchSessions', true);

      set({
        sessions: response.sessions,
        sessionsPagination: response.pagination,
        isLoading: setLoading(state.isLoading, 'searchSessions', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[SessionsSlice] Failed to search sessions:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to search sessions';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'searchSessions', false);

      set({
        isLoading: setLoading(state.isLoading, 'searchSessions', false),
        errors: setError(state.errors, 'searchSessions', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  resetSessionsPagination: () => {
    set({
      sessionsPagination: null,
      sessionsSearchQuery: '',
    });
  },

  revokeSession: async (appId: string, sessionId: string) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'revokeSession', true),
      errors: clearOpError(currentState.errors, 'revokeSession'),
    });

    try {
      await applicationsApi.revokeSession(appId, sessionId);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'revokeSession', true);

      set({
        sessions: state.sessions.filter((session) => session.id !== sessionId),
        isLoading: setLoading(state.isLoading, 'revokeSession', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[SessionsSlice] Failed to revoke session:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to revoke session';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'revokeSession', false);

      set({
        isLoading: setLoading(state.isLoading, 'revokeSession', false),
        errors: setError(state.errors, 'revokeSession', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  expireSession: (sessionId: string) => {
    const state = get();
    set({
      sessions: state.sessions.map((session) =>
        session.id === sessionId ? { ...session, status: SessionStatus.Expired } : session
      ),
    });
  },

  renewSession: async (_appId: string, sessionId: string, additionalMinutes: number) => {
    const currentState = get();

    set({
      isLoading: setLoading(currentState.isLoading, 'renewSession', true),
      errors: clearOpError(currentState.errors, 'renewSession'),
    });

    try {
      // GW-554: Call real API to extend session
      const response = await sessionsApi.renewSession(sessionId, additionalMinutes);

      const state = get();

      // Update session with response data
      set({
        sessions: state.sessions.map((session) =>
          session.id === sessionId
            ? {
                ...session,
                expiresAt: response.new_expires_at,
                durationMinutes: session.durationMinutes + additionalMinutes,
                smartTokens: response.total_tokens_spent,
              }
            : session
        ),
        isLoading: setLoading(state.isLoading, 'renewSession', false),
      });

      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'renewSession', true);
      set({
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[SessionsSlice] Failed to renew session:', error);

      const state = get();

      // GW-554: Handle specific error cases
      let errorMessage = 'Failed to renew session';
      if (error instanceof ApiError) {
        if (error.status === 402) {
          errorMessage = 'Insufficient token balance to extend session';
        } else if (error.status === 400 || error.status === 410) {
          errorMessage = 'Session is no longer active';
        } else if (error.status === 404) {
          errorMessage = 'Session not found';
        } else {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'renewSession', false);

      set({
        isLoading: setLoading(state.isLoading, 'renewSession', false),
        errors: setError(state.errors, 'renewSession', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ==========================================================================
  // Utility Actions
  // ==========================================================================

  clearError: (operation?: string) => {
    const currentState = get();

    if (operation) {
      set({
        errors: clearOpError(currentState.errors, operation),
        errorStatuses: clearErrorStatus(currentState.errorStatuses, operation),
      });
    } else {
      set({ errors: {}, errorStatuses: {} });
    }
  },

  clearActiveLaunchedSession: () => {
    set({
      activeLaunchedSession: null,
      appTabRef: null,
    });
  },
});
